import React from 'react';
import { Box } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { business } from '../../theme/tokens';

// Trimmed source is 1514 x 379 → aspect ratio ~3.99:1.
const LOGO_RATIO = 1514 / 379;

type LogoProps = {
  /** Rendered height in px. Width follows the logo's aspect ratio. */
  height?: number;
  /** Wrap in a link to the homepage (default true). */
  link?: boolean;
  /** Eager-load with high priority — use for the above-the-fold navbar instance. */
  priority?: boolean;
  sx?: Record<string, unknown>;
};

/**
 * Horizontal brand logo (orange house mark + white wordmark) from /public/brand.
 * WebP with PNG fallback; both carry transparency so it sits on any dark surface.
 */
const Logo: React.FC<LogoProps> = ({ height = 40, link = true, priority = false, sx }) => {
  const width = Math.round(height * LOGO_RATIO);

  const picture = (
    <Box
      component="picture"
      sx={{ display: 'inline-flex', lineHeight: 0, height, transition: 'height .25s ease', ...sx }}
    >
      <source type="image/webp" srcSet="/brand/logo-horizontal-480.webp 480w, /brand/logo-horizontal-960.webp 960w" sizes={`${width}px`} />
      <Box
        component="img"
        src="/brand/logo-horizontal-480.png"
        srcSet="/brand/logo-horizontal-480.png 480w, /brand/logo-horizontal-960.png 960w"
        sizes={`${width}px`}
        width={width}
        height={height}
        alt={link ? '' : business.name}
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : 'auto'}
        decoding="async"
        sx={{ height: '100%', width: 'auto', display: 'block' }}
      />
    </Box>
  );

  if (!link) return picture;

  return (
    <Box
      component={RouterLink}
      to="/"
      aria-label={`${business.name} — home`}
      sx={{ display: 'inline-flex', flexShrink: 0, textDecoration: 'none', borderRadius: '6px' }}
    >
      {picture}
    </Box>
  );
};

export default Logo;
